import { useTranslate } from "@tolgee/react";
import React from "react";
import { AskWhen, AskWhom } from "../../../clients/graphqlTypes.js";

const DEFAULT_ALLOWED_ASK_WHEN: AskWhen[] = [
  "onEmbeddableWizard",
  "onQuoteCreate",
  "onQuoteRefinement",
  "onQuoteRefinement2",
  "onQuoteRefinement3",
  "onCallAssistant",
];

export function useQuestionContol(
  allowedAskWhen: AskWhen[] = DEFAULT_ALLOWED_ASK_WHEN
) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const askWhomLabels = React.useMemo(
    (): Record<AskWhom, string> => ({
      contractee: t("Homeowner", {
        ns: "QuoteItem",
      }),
      contractor: t("Craftsmen", {
        ns: "QuoteItem",
      }),
    }),
    [t]
  );
  const askWhomOptions = React.useMemo(
    (): { value: AskWhom; label: string }[] => [
      { value: "contractor", label: askWhomLabels["contractor"] },
      { value: "contractee", label: askWhomLabels["contractee"] },
    ],
    [askWhomLabels]
  );

  const askWhenLabels = React.useMemo(
    (): Record<AskWhen, string> => ({
      onQuoteCreate: t("On quote creation", {
        ns: "QuoteItem",
      }),
      onQuoteRefinement: t("On quote refinement", { ns: "QuoteItem" }),
      onQuoteRefinement2: t("On quote refinement2" as any, { ns: "QuoteItem" }),
      onQuoteRefinement3: t("On quote refinement3" as any, { ns: "QuoteItem" }),
      onEmbeddableWizard: t("On embeddable wizard", { ns: "QuoteItem" }),
      onCallAssistant: t("On call assistant", { ns: "QuoteItem" }),
    }),
    [t]
  );

  const askWhenOptions = React.useMemo(
    (): { value: AskWhen; label: string }[] =>
      allowedAskWhen.map(value => ({
        value,
        label: askWhenLabels[value],
      })),
    [allowedAskWhen, askWhenLabels]
  );

  return {
    askWhomLabels,
    askWhomOptions,
    askWhenLabels,
    askWhenOptions,
  };
}

export function getQuestionControlLabel(
  value: { askWhen: AskWhen[]; askWhom: AskWhom[] }[],
  {
    askWhomLabels,
    askWhenLabels,
  }: {
    askWhomLabels: Record<AskWhom, string>;
    askWhenLabels: Record<AskWhen, string>;
  }
) {
  const [first, ...other] = value ?? [];
  const isAnyQuestionControlDefined = (value ?? []).some(
    v => v.askWhom.length > 0 || v.askWhen.length > 0
  );
  return first && isAnyQuestionControlDefined
    ? `${[first.askWhen.length > 0 ? first.askWhen.map(when => askWhenLabels[when]).join(", ") : "–", first.askWhom.length > 0 ? first.askWhom.map(whom => askWhomLabels[whom]).join(", ") : "–"].join(" / ")}${other.length > 0 ? ` (+${other.length})` : ""}`
    : "";
}
